import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { ArrowRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { MAIN_NAV } from '@/constants/navigation'
import { siteConfig } from '@/constants/site'

const DESKTOP_NAV = MAIN_NAV.filter(link => link.href !== '/contact')

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const toggleRef = useRef<HTMLButtonElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    useEffect(() => {
        const media = window.matchMedia('(min-width: 1024px)')
        const onChange = (event: MediaQueryListEvent) => {
            if (event.matches) setOpen(false)
        }

        media.addEventListener('change', onChange)

        return () => media.removeEventListener('change', onChange)
    }, [])

    useEffect(() => {
        if (!open) return

        const previousOverflow = document.body.style.overflow
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return

            setOpen(false)
            toggleRef.current?.focus()
        }

        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', onKeyDown)
        panelRef.current?.querySelector<HTMLElement>('a')?.focus()

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [open])

    const isActive = (href: string): boolean => {
        if (href.includes('#')) return false
        if (href === '/') return router.pathname === '/'

        return router.pathname === href || router.pathname.startsWith(`${href}/`)
    }

    return (
        <header
            className={cn(
                'sticky top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-300',
                scrolled || open ? 'border-border/70 bg-background/92 shadow-[0_8px_30px_-20px_rgba(74,13,24,0.35)] backdrop-blur-md' : 'border-transparent bg-background/0'
            )}
        >
            <div className='mx-auto flex h-20 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8'>
                <Logo />

                <nav aria-label="Main" className='hidden lg:block'>
                    <ul className='flex items-center gap-1'>
                        {DESKTOP_NAV.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    aria-current={isActive(link.href) ? 'page' : undefined}
                                    className={cn(
                                        'rounded-full px-4 py-2 text-sm font-medium text-foreground/80 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                        isActive(link.href) && 'text-primary'
                                    )}
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-3'>
                    <ButtonLink href='/contact' size='sm' className='hidden sm:inline-flex'>
                        Plan your wedding
                    </ButtonLink>

                    <button
                        ref={toggleRef}
                        type='button'
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? "Close menu" : "Open menu"}
                        onClick={() => setOpen(value => !value)}
                        className='inline-flex size-11 items-center justify-center rounded-full border border-border bg-card/70 text-foreground transition-colors hover:border-primary/40 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden'
                    >
                        {open ? <X className='size-5' /> : <Menu className='size-5' />}
                    </button>
                </div>
            </div>

            <div
                id='mobile-menu'
                ref={panelRef}
                hidden={!open}
                className='h-[calc(100dvh-5rem)] overflow-y-auto border-t border-border/70 bg-background lg:hidden'
            >
                <nav aria-label="Mobile" className='mx-auto max-w-7xl px-5 pb-12 pt-4 sm:px-8'>
                    <ul>
                        {MAIN_NAV.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    onClick={() => setOpen(false)}
                                    aria-current={isActive(link.href) ? 'page' : undefined}
                                    className={cn(
                                        'flex items-center justify-between border-b border-border/70 py-4 font-heading text-3xl text-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                        isActive(link.href) && 'text-primary'
                                    )}
                                >
                                    {link.label}
                                    <ArrowRight aria-hidden='true' className='size-5 text-gold' />
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <ButtonLink href='/contact' size='lg' className='mt-8 w-full' onClick={() => setOpen(false)}>
                        Plan your wedding
                    </ButtonLink>

                    <p className='mt-6 text-sm text-muted-foreground'>
                        {siteConfig.studio}
                    </p>
                </nav>
            </div>
        </header>
    )
}
